import { useEffect, useState } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import { ImageItem } from '../types/common/image-item';
import { Logger } from '../utils/logger';

const generateThumbnail = async (data: string, MAX_WIDTH = 150, MAX_HEIGHT = 150): Promise<string> => {
	// We create an image to receive the Data URI
	return await new Promise((resolve, reject) => {
		const img = new Image();
		img.crossOrigin = 'anonymous';

		const thumbnail = '';
		// When the event "onload" is triggered we can resize the image.

		img.onload = () => {
			// We create a canvas and get its context.
			Logger.info('[generateThumbnail] image loaded');
			const canvas = document.createElement('canvas');
			const ctx = canvas.getContext('2d');

			if (img.width > img.height) {
				canvas.width = MAX_WIDTH;
				canvas.height = (MAX_HEIGHT / img.width) * img.height;
			} else {
				canvas.width = (MAX_WIDTH / img.height) * img.width;
				canvas.height = MAX_HEIGHT;
			}

			// We resize the image with the canvas method drawImage();
			ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

			const base64Data = canvas.toDataURL('image/jpeg');
			Logger.info('[generateThumbnail] thumbnail generated');
			resolve(base64Data);
		};

		img.onerror = (err) => {
			Logger.error('[generateThumbnail] image thumbnail creation error', err);
			const error = new Error('[generateThumbnail] image thumbnail creation error');
			datadogRum.addError(error);
			reject('error');
		};

		// We put the Data URI in the image's src attribute
		img.src = data;
		return thumbnail;
	});
};

const loadThumbnail = async (image: string) => {
    let thumbnail;
    try {
        thumbnail = await generateThumbnail(image);
    } catch (err) {
        Logger.error('[useImageGalleryItems] thumbnail generation exception', err);
        const error = new Error('[useImageGalleryItems] thumbnail generation exception');
        datadogRum.addError(error);
        throw err;
    }
    return thumbnail;
};

export const useImageGalleryItems = (base64Images: Array<string>) => {
	const [images, setImages] = useState([]);
	const [isLoadingImages, setIsLoadingImages] = useState(true);
	const [hasLoadingError, setHasLoadingError] = useState(false);

	const injectClass = (items: Array<ImageItem>) => {
		return items.map((item) => {
			return {
				...item,
				originalClass: 'idw-image-gallery dd-privacy-mask',
				thumbnailClass: 'dd-privacy-mask'
			};
		});
	};

	useEffect(() => {
		const galleryItems = Promise.all(
			base64Images.map(async (base64Image) => {
				const imageItem: ImageItem = {
					original: base64Image,
					thumbnail: ''
				};
				try {
					imageItem.thumbnail = await loadThumbnail(base64Image);
				} catch (err) {
					console.error('Thumbnail creation failed', err);
				}
				return imageItem;
			})
		);

		galleryItems
			.then((items) => {
				items = injectClass(items);
				setImages([...items]);
				setIsLoadingImages(false);
			})
			.catch((err) => {
				console.error(err);
				setImages([]);
				setIsLoadingImages(false);
				setHasLoadingError(true);
			});
	}, [base64Images]);
	return {
		items: images,
		isLoadingImages,
		hasLoadingError
	};
}
